import React from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";

const formatCurrency = (amount, currency) => {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    return "Tuition details on request";
  }
  const formattedAmount = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return `${currency || ""} ${formattedAmount}`.trim();
};

const renderDescription = (course) => {
  if (!course) return "";

  if (course.universityName) {
    return `${course.universityName}.`;
  }
  if (course.concentration) {
    return course.concentration;
  }
  if (course.levelOfStudy && course.location) {
    return `${course.levelOfStudy} program located in ${course.location}.`;
  }
  return `Study ${course.programName}.`;
};

const resolveAvatarUrl = (url = "") => {
  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }
  const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${API_BASE_URL}${normalized}`;
};

const getApplicantInitials = (name = "", email = "") => {
  const source = name || email || "";
  if (!source) {
    return "?";
  }
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) {
    return source.charAt(0).toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(
    0
  )}`.toUpperCase();
};

const hasAppliedToCourse = (appliedCourseIds, courseId) => {
  const normalizedId =
    typeof courseId === "string" ? courseId : courseId ? String(courseId) : "";
  if (!normalizedId) {
    return false;
  }
  if (appliedCourseIds instanceof Set) {
    return appliedCourseIds.has(normalizedId);
  }
  if (Array.isArray(appliedCourseIds)) {
    return appliedCourseIds.includes(normalizedId);
  }
  if (appliedCourseIds && typeof appliedCourseIds === "object") {
    return Boolean(appliedCourseIds[normalizedId]);
  }
  return false;
};

const CourseItemList = ({
  courses,
  loading,
  error,
  onApply,
  recentApplicants = {},
  recentApplicantsLoading = false,
  appliedCourseIds,
}) => {
  const handleApplyClick = (course) => {
    if (typeof onApply === "function") {
      onApply(course);
    }
  };

  if (loading) {
    return (
      <Col md="12">
        <div className="course-item loading-state">
          <Spinner animation="border" role="status" size="sm" />
          <span className="ml-2">Loading courses…</span>
        </div>
      </Col>
    );
  }

  if (error) {
    return null;
  }

  if (!courses || courses.length === 0) {
    return (
      <Col md="12">
        <div className="course-item empty-state">
          <p>No courses matched your search.</p>
        </div>
      </Col>
    );
  }

  return (
    <>
      {courses.map((course) => {
        const alreadyApplied = hasAppliedToCourse(appliedCourseIds, course._id);
        return (
          <Col lg="6" md="12" key={course._id}>
            <div className="course-item">
              <div className="course-content">
                <h6 className="heading">
                  <Link to={`${process.env.PUBLIC_URL}/courses/${course._id}`}>
                    {course.programName}
                  </Link>
                </h6>
                <div className="course-price mb-2">
                  <p style={{ fontWeight: 700, color: "red", margin: 0 }}>
                    {formatCurrency(
                      course.tuitionFeePerYear || course.tuitionFeePerSemester,
                      course.currency
                    )}
                  </p>
                </div>
                <p className="desc">{renderDescription(course)}</p>
                <div className="course-face d-flex justify-content-between">
                  <div className="duration">
                    <p>
                      <i className="las la-user-graduate"></i>
                      {course.levelOfStudy || "Program"}
                    </p>
                  </div>
                  <div className="student">
                    <p>
                      <i className="las la-map-marker-alt"></i>
                      {course.location || "Multiple locations"}
                    </p>
                  </div>
                  <div className="duration">
                    <p>
                      <i className="las la-clock"></i>
                      {course.duration
                        ? `${course.duration} months`
                        : "Duration TBD"}
                    </p>
                  </div>
                </div>
                <div className="course-actions d-flex justify-content-between mt-3">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      alreadyApplied ? "btn-success" : "btn-outline-secondary"
                    }`}
                    onClick={() => handleApplyClick(course)}
                    disabled={alreadyApplied}
                  >
                    {alreadyApplied ? "Applied" : "Apply Now"}
                  </button>
                  <Link
                    to={`${process.env.PUBLIC_URL}/courses/${course._id}`}
                    className="btn btn-link btn-sm"
                  >
                    View details
                  </Link>
                </div>
                <div className="course-foot mt-3">
                  {}
                  <div className="recent-applicants">
                    {recentApplicantsLoading ? (
                      <Spinner animation="border" role="status" size="sm" />
                    ) : (
                      (() => {
                        const applicants = Array.isArray(
                          recentApplicants[course._id]
                        )
                          ? recentApplicants[course._id]
                          : [];
                        if (!applicants.length) {
                          return null;
                        }
                        const displayApplicants = applicants.slice(0, 5);

                        return (
                          <Row
                            className="avatar-group"
                            style={{ flexDirection: "row" }}
                            aria-label={`${applicants.length} applicants in the last six months`}
                          >
                            {displayApplicants.map((applicant, index) => {
                              const avatarUrl = resolveAvatarUrl(
                                applicant.avatarUrl || ""
                              );
                              const initials = getApplicantInitials(
                                applicant.name,
                                applicant.applicantEmail
                              );
                              const key =
                                applicant.applicantEmail ||
                                `${course._id}-${index}`;
                              return (
                                <div
                                  className={`avatar-item${
                                    avatarUrl ? "" : " no-image"
                                  }`}
                                  style={{ marginRight: 5 }}
                                  key={key}
                                  title={
                                    applicant.name ||
                                    applicant.applicantEmail ||
                                    "Applicant"
                                  }
                                >
                                  {avatarUrl ? (
                                    <img
                                      src={avatarUrl}
                                      alt={applicant.name || "Applicant"}
                                    />
                                  ) : (
                                    <div
                                      className="avatar"
                                      style={{
                                        borderRadius: "50%",
                                        background: "#dedede",
                                        padding: 5,
                                        cursor: "pointer",
                                      }}
                                    >
                                      <span style={{ textAlign: "center" }}>
                                        {initials}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {applicants.length > displayApplicants.length && (
                              <div className="avatar-item counter">
                                +{applicants.length - displayApplicants.length}
                              </div>
                            )}
                          </Row>
                        );
                      })()
                    )}
                  </div>
                  <div className="recent-count" />
                </div>
              </div>
            </div>
          </Col>
        );
      })}
    </>
  );
};

export default CourseItemList;
